/**
 * istDateUtils.js
 *
 * Problem: The deployed Linux server runs in UTC. Any code that calls
 * `new Date().toTimeString()`, `date.getHours()`, `date.getDate()`, etc.
 * will return UTC values — 5h 30min behind IST.
 *
 * Solution: Always derive date/time strings by projecting into Asia/Kolkata
 * using the Intl API, which works correctly in ANY server timezone.
 */

/**
 * Given any Date object, return its components expressed in IST (Asia/Kolkata).
 * Safe on both local (IST) and deployed (UTC) environments.
 *
 * @param {Date} date
 * @returns {{
 *   dd: string,
 *   mm: string,
 *   yyyy: string,
 *   isoDate: string,   // "YYYY-MM-DD"  — for formatDateForApi / customStartDate
 *   ddmmyyyy: string,  // "DD/MM/YYYY"  — for the /location/all date param
 *   time: string,      // "HH:MM:SS"    — for startTime / endTime params
 * }}
 */
export const getISTDateParts = (date) => {
  // Project the UTC instant into IST via toLocaleString; re-parse so that
  // .getHours() / .getDate() etc. return IST values regardless of server TZ.
  const istString = date.toLocaleString("en-US", { timeZone: "Asia/Kolkata" });
  const istDate = new Date(istString);

  const dd   = String(istDate.getDate()).padStart(2, "0");
  const mm   = String(istDate.getMonth() + 1).padStart(2, "0");
  const yyyy = String(istDate.getFullYear());
  const hh   = String(istDate.getHours()).padStart(2, "0");
  const min  = String(istDate.getMinutes()).padStart(2, "0");
  const ss   = String(istDate.getSeconds()).padStart(2, "0");

  return {
    dd,
    mm,
    yyyy,
    isoDate:  `${yyyy}-${mm}-${dd}`,  // "2026-03-06"
    ddmmyyyy: `${dd}/${mm}/${yyyy}`,  // "06/03/2026"
    time:     `${hh}:${min}:${ss}`,  // "15:14:00"
  };
};

/**
 * Convenience: returns IST parts for the current moment.
 */
export const getISTNowParts = () => getISTDateParts(new Date());

/**
 * Format any Date as "YYYY-MM-DD" in IST.
 * Use this for API range params (customStartDate / customEndDate).
 */
export const formatISODateIST = (date) => getISTDateParts(date).isoDate;
